/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { platformMcpStartOnboarding } from "../funcs/platformMcpStartOnboarding.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PlatformMCPOnboardingState } from "../models/components/platformmcponboardingstate.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  StartPlatformMCPOnboardingRequest,
  StartPlatformMCPOnboardingSecurity,
} from "../models/operations/startplatformmcponboarding.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type StartPlatformMCPOnboardingMutationVariables = {
  request: StartPlatformMCPOnboardingRequest;
  security?: StartPlatformMCPOnboardingSecurity | undefined;
  options?: RequestOptions;
};

export type StartPlatformMCPOnboardingMutationData = PlatformMCPOnboardingState;

export type StartPlatformMCPOnboardingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * startOnboarding platformMcp
 *
 * @remarks
 * Create or resume the current user's durable Platform MCP onboarding workflow.
 */
export function useStartPlatformMCPOnboardingMutation(
  options?: MutationHookOptions<
    StartPlatformMCPOnboardingMutationData,
    StartPlatformMCPOnboardingMutationError,
    StartPlatformMCPOnboardingMutationVariables
  >,
): UseMutationResult<
  StartPlatformMCPOnboardingMutationData,
  StartPlatformMCPOnboardingMutationError,
  StartPlatformMCPOnboardingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildStartPlatformMCPOnboardingMutation(client, options),
    ...options,
  });
}

export function mutationKeyStartPlatformMCPOnboarding(): MutationKey {
  return ["@gram/client", "platformMcp", "startOnboarding"];
}

export function buildStartPlatformMCPOnboardingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: StartPlatformMCPOnboardingMutationVariables,
  ) => Promise<StartPlatformMCPOnboardingMutationData>;
} {
  return {
    mutationKey: mutationKeyStartPlatformMCPOnboarding(),
    mutationFn: function startPlatformMCPOnboardingMutationFn({
      request,
      security,
      options,
    }): Promise<StartPlatformMCPOnboardingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(platformMcpStartOnboarding(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
